import { useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Grid,
  Group,
  JsonInput,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconFileUpload,
  IconKey,
  IconPlayerPlay,
  IconX,
} from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import { DoclenzExtractService } from '@services/DoclenzExtractService'
import type { DoclenzKeyService } from '@services/DoclenzKeyService'
import type { ExtractionResultDto } from '@domain/job/JobDto'
import { PLAYGROUND_TENANT } from '../../AppConstants'

/**
 * Sobe um documento e mostra o que o doclenz extraiu.
 *
 * Usa o `POST /v1/extract` real, com uma API key de tenant — o mesmo caminho que os produtos
 * percorrem. O que se vê aqui é o que o consumidor veria, cobrança de UPs inclusive.
 */
export function PlaygroundView() {
  const extractApi = useArchbaseRemoteServiceApi<DoclenzExtractService>(API_TYPE.DoclenzExtract)
  const keyApi = useArchbaseRemoteServiceApi<DoclenzKeyService>(API_TYPE.DoclenzKey)

  const [file, setFile] = useState<File | null>(null)
  const [schema, setSchema] = useState('')
  const [pipeline, setPipeline] = useState<string | null>(null)
  /** Motor por requisição (ADR-007 D-8). Null = o padrão configurado na plataforma. */
  const [extractor, setExtractor] = useState<string | null>(null)
  const [result, setResult] = useState<ExtractionResultDto | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasKey, setHasKey] = useState(!!DoclenzExtractService.playgroundKey())
  const [provisioning, setProvisioning] = useState(false)
  /**
   * Chaves do tenant `playground` que já existem no servidor.
   *
   * A chave em claro só existe neste navegador — o backend nunca a devolve de novo. Então em
   * outro navegador, ou depois de limpar os dados, é preciso saber se já há uma chave lá antes
   * de criar outra: sem esta consulta a tela criava uma segunda chave em silêncio, e elas
   * acumulavam órfãs no servidor.
   */
  const [orphanKeyIds, setOrphanKeyIds] = useState<string[] | null>(null)

  useEffect(() => {
    if (hasKey) {
      setOrphanKeyIds([])
      return
    }
    let cancelado = false
    keyApi
      .findAll()
      .then((r) => {
        if (cancelado) return
        const ids = (r.keys || [])
          .filter((k) => k.record.tenant_id === PLAYGROUND_TENANT)
          .map((k) => k.key_id)
        setOrphanKeyIds(ids)
      })
      .catch(() => !cancelado && setOrphanKeyIds([]))
    return () => {
      cancelado = true
    }
  }, [hasKey, keyApi])

  /**
   * Cria a chave do playground — é o único momento em que o backend devolve o valor em claro.
   *
   * Revoga antes as chaves órfãs do mesmo tenant: como nenhuma delas é recuperável, manter uma
   * chave válida que ninguém possui é só superfície de ataque.
   */
  const provisionKey = async () => {
    setProvisioning(true)
    try {
      for (const keyId of orphanKeyIds ?? []) {
        await keyApi.revoke(keyId).catch(() => undefined)
      }
      const created = await keyApi.create({
        tenant_id: PLAYGROUND_TENANT,
        product: 'Doclenz Admin — playground',
        requests_per_minute: 30,
        ups_per_day: 500,
        ups_per_month: 0,
        // extractor_choice habilita a troca de motor por requisição — é o que permite
        // comparar xberg e Tika sobre o mesmo documento aqui dentro.
        features: ['ocr', 'llm_enrichment', 'extractor_choice'],
        webhook_url: null,
      })
      DoclenzExtractService.setPlaygroundKey(created.api_key)
      setOrphanKeyIds([])
      setHasKey(true)
      notifications.show({ message: 'Chave do playground criada.', color: 'green' })
    } catch (err: any) {
      notifications.show({
        title: 'Falha ao criar a chave',
        message: err?.message || 'Erro desconhecido',
        color: 'red',
      })
    } finally {
      setProvisioning(false)
    }
  }

  const run = async () => {
    if (!file) return
    setRunning(true)
    setError(null)
    setResult(null)
    try {
      const extraction = await extractApi.extract(file, {
        schema: schema.trim() || undefined,
        pipeline: pipeline || undefined,
        extractor: extractor || undefined,
      })
      setResult(extraction)
    } catch (err: any) {
      const payload = err?.response?.data
      setError(payload?.message ? `${payload.code}: ${payload.message}` : err?.message || 'Falha na extração.')
    } finally {
      setRunning(false)
    }
  }

  if (!hasKey) {
    // Enquanto a consulta ao servidor não volta, não se sabe qual dos dois casos é — e mostrar
    // "criar chave" antes disso é o que causava a duplicata.
    if (orphanKeyIds === null) {
      return (
        <Stack p="md" gap="lg">
          <Title order={3}>Playground</Title>
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        </Stack>
      )
    }

    const jaExisteNoServidor = orphanKeyIds.length > 0

    return (
      <Stack p="md" gap="lg">
        <Title order={3}>Playground</Title>
        <Alert
          icon={<IconKey size={16} />}
          color={jaExisteNoServidor ? 'yellow' : 'blue'}
          variant="light"
        >
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              O playground extrai pelo <Code>POST /v1/extract</Code> real, que exige uma API key de
              tenant — a chave de admin não serve. A chave fica dedicada ao tenant{' '}
              <b>{PLAYGROUND_TENANT}</b>, para o consumo de teste não se misturar ao de cliente.
            </Text>

            {jaExisteNoServidor ? (
              <>
                <Text size="sm">
                  Já existe {orphanKeyIds.length === 1 ? 'uma chave' : `${orphanKeyIds.length} chaves`}{' '}
                  desse tenant no servidor, mas <b>este navegador não a tem</b>. A chave em claro só
                  aparece no momento da criação e o servidor nunca a devolve — então não há como
                  recuperá-la aqui.
                </Text>
                <Text size="sm">
                  Seguir revoga {orphanKeyIds.length === 1 ? 'a chave antiga' : 'as chaves antigas'} e
                  cria uma nova. Nada se perde: a antiga não estava em uso por ninguém, já que
                  ninguém a possui.
                </Text>
                <Button color="yellow" onClick={provisionKey} loading={provisioning}>
                  Revogar e criar nova chave
                </Button>
              </>
            ) : (
              <Button onClick={provisionKey} loading={provisioning}>
                Criar chave do playground
              </Button>
            )}
          </Stack>
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack p="md" gap="lg">
      <Group justify="space-between">
        <Title order={3}>Playground</Title>
        <Tooltip label="Descarta a chave guardada neste navegador; a do servidor continua existindo">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => {
              DoclenzExtractService.clearPlaygroundKey()
              setHasKey(false)
            }}
          >
            Esquecer chave
          </Button>
        </Tooltip>
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card withBorder radius="md" p="md">
            <Stack>
              <Dropzone onDrop={(files) => setFile(files[0] ?? null)} multiple={false} maxSize={50 * 1024 ** 2}>
                <Group justify="center" gap="sm" mih={120} style={{ pointerEvents: 'none' }}>
                  <Dropzone.Accept>
                    <IconFileUpload size={36} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size={36} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconFileUpload size={36} stroke={1.5} />
                  </Dropzone.Idle>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>
                      {file ? file.name : 'Arraste um documento ou clique'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      PDF, DOCX, XLSX, imagens — até 50 MB
                    </Text>
                  </Stack>
                </Group>
              </Dropzone>

              <Select
                label="Pipeline"
                description="Em branco = detecção automática"
                data={['AUTO', 'TEXT', 'OCR', 'VISION', 'AUDIO']}
                value={pipeline}
                onChange={setPipeline}
                clearable
              />

              <Select
                label="Motor de extração"
                description="Em branco = o padrão configurado. Trocar aqui compara motores sobre o mesmo documento."
                data={[
                  { value: 'xberg', label: 'xberg (nativo, Rust via FFM)' },
                  { value: 'tika', label: 'Tika (JVM puro, plano B)' },
                  { value: 'stub', label: 'stub (PDFBox, só PDF)' },
                ]}
                value={extractor}
                onChange={setExtractor}
                clearable
              />

              <JsonInput
                label="JSON Schema (opcional)"
                description="Com schema, a API devolve `structured` — é o contrato que o VendaX consome"
                placeholder='{"type":"object","properties":{...}}'
                value={schema}
                onChange={setSchema}
                autosize
                minRows={4}
                formatOnBlur
              />

              <Button
                leftSection={<IconPlayerPlay size={16} />}
                onClick={run}
                loading={running}
                disabled={!file}
              >
                Extrair
              </Button>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card withBorder radius="md" p="md" mih={300}>
            {running && (
              <Group justify="center" p="xl">
                <Loader />
              </Group>
            )}

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}

            {!running && !error && !result && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                O resultado da extração aparece aqui.
              </Text>
            )}

            {result && <ExtractionResult result={result} />}
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

function ExtractionResult({ result }: { result: ExtractionResultDto }) {
  return (
    <Stack gap="md">
      <Group gap="xs">
        <Badge color="green" variant="light">
          {result.status}
        </Badge>
        <Badge variant="light">{result.processing_ms} ms</Badge>
        {result.source.pages != null && <Badge variant="light">{result.source.pages} páginas</Badge>}
        <Badge variant="light">{result.chunks.length} chunks</Badge>
        {result.billing && <Badge color="grape" variant="light">{result.billing.ups_consumed} UPs</Badge>}
      </Group>

      <Tabs defaultValue="chunks">
        <Tabs.List>
          <Tabs.Tab value="chunks">Chunks</Tabs.Tab>
          {result.structured != null && <Tabs.Tab value="structured">Estruturado</Tabs.Tab>}
          <Tabs.Tab value="source">Origem</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="chunks" pt="sm">
          <ScrollArea h={420}>
            <Stack gap="xs">
              {result.chunks.map((chunk) => (
                <Card key={chunk.hash} withBorder radius="sm" p="xs">
                  <Group gap="xs" mb={4}>
                    <Badge size="xs" variant="light">
                      #{chunk.index}
                    </Badge>
                    <Badge size="xs" variant="outline">
                      {chunk.type}
                    </Badge>
                    {chunk.page != null && (
                      <Badge size="xs" variant="outline" color="gray">
                        p.{chunk.page}
                      </Badge>
                    )}
                    <Text size="xs" c="dimmed">
                      {chunk.tokens} tokens
                    </Text>
                  </Group>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {chunk.text}
                  </Text>
                </Card>
              ))}
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        {result.structured != null && (
          <Tabs.Panel value="structured" pt="sm">
            <ScrollArea h={420}>
              <Code block>{JSON.stringify(result.structured, null, 2)}</Code>
            </ScrollArea>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="source" pt="sm">
          <Table withTableBorder>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th w={160}>Arquivo</Table.Th>
                <Table.Td>{result.source.filename}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Tipo</Table.Th>
                <Table.Td>{result.source.mime_type}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Tamanho</Table.Th>
                <Table.Td>{(result.source.size_bytes / 1024).toFixed(1)} KB</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Idioma</Table.Th>
                <Table.Td>{result.metadata.language}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>SHA-256</Table.Th>
                <Table.Td>
                  <Code style={{ wordBreak: 'break-all' }}>{result.source.hash_sha256}</Code>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}
